import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import ListItem from "@material-ui/core/ListItem";
import Grid from "@material-ui/core/Grid";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { useFormik } from "formik";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import uuidv4 from "uuid/v4";

import { queryInstances } from "../../../utils/appsyncOperations";
import * as constants from "../../../constants";

const MutateIncidentReportVehicle = ({
  isOpen,
  onClose,
  onSave,
  description = "",
  make = "",
  model = "",
  year = "",
  color = "",
  vin = "",
  registeredOwner = "",
  type = null,
  status = null,
  vehicleLicenseNumber = "",
  vehicleLicenseState = null,
  id = uuidv4(),
}) => {
  const classes = useStyles();

  const [vehicleType, setVehicleType] = useState(type);
  const [vehicleStatus, setVehicleStatus] = useState(status);
  const [licenseState, setLicenseState] = useState(vehicleLicenseState);

  const snackbar = useSnackbar();
  const formData = useFormik({
    initialValues: {
      description,
      make,
      model,
      year,
      color,
      vin,
      registeredOwner,
      vehicleLicenseNumber,
    },
    onSubmit: (values) => formSubmit(values),
    validationSchema: yup.object({
      description: yup
        .string()
        .max(140, "Must be less than 140 characters")
        .required("Required"),
      make: yup.string().max(140, "Must be less than 140 characters"),
      model: yup.string().max(140, "Must be less than 140 characters"),
      color: yup.string().max(140, "Must be less than 140 characters"),
      vin: yup.string().max(140, "Must be less than 140 characters"),
      registeredOwner: yup
        .string()
        .max(140, "Must be less than 140 characters"),
      vehicleLicenseNumber: yup
        .string()
        .max(140, "Must be less than 140 characters"),
      year: yup.number().integer().min(0, "Cannot be a negative number"),
    }),
  });

  useEffect(() => {
    if (formData.isSubmitting && Object.values(formData.errors).length > 0) {
      snackbar.enqueueSnackbar("Please fix form errors", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [formData.isValidating, formData.errors]);

  const formSubmit = (values) => {
    const vehicle = {
      description: values.description,
      make: values.make,
      model: values.model,
      year: values.year,
      color: values.color,
      vin: values.vin,
      registeredOwner: values.registeredOwner,
      vehicleLicense: {
        number: values.vehicleLicenseNumber,
        state: licenseState,
      },
      type: vehicleType,
      status: vehicleStatus,
      id,
    };
    onSave(vehicle);
  };

  const makeError = formData.touched.make && formData.errors.make;
  const descriptionError =
    formData.touched.description && formData.errors.description;
  const modelError = formData.touched.model && formData.errors.model;
  const yearError = formData.touched.year && formData.errors.year;
  const colorError = formData.touched.color && formData.errors.color;
  const vinError = formData.touched.vin && formData.errors.vin;
  const registeredOwnerError =
    formData.touched.registeredOwner && formData.errors.registeredOwner;
  const vehicleLicenseNumberError =
    formData.touched.vehicleLicenseNumber &&
    formData.errors.vehicleLicenseNumber;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a vehicle</DialogTitle>
      <DialogContent>
        <List>
          <ListSubheader disableSticky>General</ListSubheader>
          <ListItem>
            <TextField
              fullWidth
              required
              id={"description"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.description}
              onChange={formData.handleChange}
              error={descriptionError}
              helperText={descriptionError ? formData.errors.description : null}
              label={"Description"}
            />
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={vehicleStatus}
                    onChange={(e) => setVehicleStatus(e.target.value)}
                  >
                    {Object.values(constants.vehicle.status).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                  >
                    {Object.values(constants.vehicle.type).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"make"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.make}
                  onChange={formData.handleChange}
                  error={makeError}
                  helperText={makeError ? formData.errors.make : null}
                  label={"Make"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"model"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.model}
                  onChange={formData.handleChange}
                  error={modelError}
                  helperText={modelError ? formData.errors.model : null}
                  label={"Model"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type={"number"}
                  fullWidth
                  id={"year"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.year}
                  onChange={formData.handleChange}
                  error={yearError}
                  helperText={yearError ? formData.errors.year : null}
                  label={"Year"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"color"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.color}
                  onChange={formData.handleChange}
                  error={colorError}
                  helperText={colorError ? formData.errors.color : null}
                  label={"Color"}
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListSubheader disableSticky>Registration</ListSubheader>
          <ListItem>
            <TextField
              fullWidth
              id={"vin"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.vin}
              onChange={formData.handleChange}
              error={vinError}
              helperText={vinError ? formData.errors.vin : null}
              label={"VIN"}
            />
          </ListItem>
          <ListItem>
            <TextField
              fullWidth
              id={"registeredOwner"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.registeredOwner}
              onChange={formData.handleChange}
              error={registeredOwnerError}
              helperText={
                registeredOwnerError ? formData.errors.registeredOwner : null
              }
              label={"Registered Owner"}
            />
          </ListItem>
          <ListSubheader disableSticky>Vehicle License</ListSubheader>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"vehicleLicenseNumber"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.vehicleLicenseNumber}
                  onChange={formData.handleChange}
                  error={vehicleLicenseNumberError}
                  helperText={
                    vehicleLicenseNumberError
                      ? formData.errors.vehicleLicenseNumber
                      : null
                  }
                  label={"Number"}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={licenseState}
                    onChange={(e) => setLicenseState(e.target.value)}
                  >
                    {Object.values(constants.states).map((type) => (
                      <MenuItem value={type.abbreviation}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={formData.submitForm} color="primary">
          Save
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
  },
}));

export default MutateIncidentReportVehicle;
